// src/components/navigation/TopMenuBar.tsx
import React, { useState } from 'react';
import MainMenu from './MainMenu';
import AccountMenu from './AccountMenu';
import { AboutModal } from '../about/AboutModal';
import { useTools } from '../../context/ToolContext';
import { useLayerDrawer, DrawerHeight } from '../../easymap/layerDrawer';
import { usePermissions } from '../../context/PermissionContext';
import { navigationConfig } from './navigationConfig';
import { filterNavByPermission, NavItem as FilterNavItem } from './filterNavByPermission';
import { resolveCrossAppUrl } from '../../utils/crossAppUrlResolver';
import logo from '../../assets/easymap_logo.png';
import './TopMenuBar.scss';

type NavItem = {
  type: 'link' | 'group' | 'external' | 'cross-app';
  label: string;
  tool?: string;
  action?: string;
  href?: string;
  payload?: any;
  children?: NavItem[];
  requires?: { feature: string; operation: string };
};

interface TopMenuBarProps {
  config: {
    main: NavItem[];
    account: NavItem[];
  };
}

const TopMenuBar: React.FC<TopMenuBarProps> = ({ config }) => {
  const { openTool } = useTools();
  const { setDrawerHeight } = useLayerDrawer();
  const { isAdmin, canDo } = usePermissions();
  const [showAbout, setShowAbout] = useState(false);

  // be defensive – if navigationConfig somehow didn't load,
  // don't crash the whole app
  const cfg = navigationConfig ?? { main: [], account: [], admin: [] };

  // Filter nav items by their `requires` permission via the shared util — same
  // function the /api/navigation backend route uses, so the menu rendered here
  // and the menu served to CMS are bit-for-bit identical. isAdmin still shortcuts
  // to true via canDo so admins see everything.
  const filterByPermission = (items: any[]) =>
    filterNavByPermission(items as readonly FilterNavItem[], canDo);

  // normalize menu items so they always have an onClick
  const attachHandlers = (items: any[]): any[] =>
    items.map((item: any) => {
      const next: any = { ...item };

      // recurse into children first
      if (next.children) {
        next.children = attachHandlers(next.children);
      }

      // figure out what key this item wants to open
      const key = next.tool || next.action;

      if (key) {
        // all tools (including onLogout) go through context
        next.onClick = () => openTool(key, next.payload);
        return next;
      }

      // external links open in new tab (e.g. GNWT highways)
      if (next.type === 'external' && next.href) {
        next.onClick = () => window.open(next.href, '_blank', 'noopener,noreferrer');
      }

      // cross-app links open in same tab (apps with shared auth)
      if (next.type === 'cross-app' && next.href) {
        const resolvedUrl = resolveCrossAppUrl(next.href);
        if (resolvedUrl) {
          next.onClick = () => window.location.href = resolvedUrl;
        } else {
          console.error(`[TopMenuBar] Failed to resolve cross-app URL: ${next.href}`);
        }
      }

      return next;
    });

  // Build main menu, adding admin submenu at bottom if user is admin
  const mainMenuItems = [...cfg.main];
  if (isAdmin && cfg.admin && cfg.admin.length > 0) {
    mainMenuItems.push({
      type: 'group',
      label: 'Admin',
      children: cfg.admin,
    } as any);
  }

  const mainMenu = attachHandlers(filterByPermission(mainMenuItems));
  const accountMenu = attachHandlers(filterByPermission([...cfg.account]));

  return (
    <div className="topmenubar">
      <div className="topmenubar-left">
        <MainMenu menu={mainMenu} />
      </div>

      <div className="topmenubar-center">
        <button
          onClick={() => setShowAbout(true)}
          className="topmenubar-about-btn"
          title="About EasyMap 3"
        >
          <img src={logo} alt="Intellifire EasyMap 3" className="topmenubar-logo" />
        </button>
        <span className="topmenubar-title">Intellifire EasyMap 3</span>
        {showAbout && <AboutModal onClose={() => setShowAbout(false)} />}
      </div>

      <div className="topmenubar-right">
        <AccountMenu menu={accountMenu} />
     </div>
    </div>
  );
};

export default TopMenuBar;
